import { createElement, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { loadStepperAdventure } from "@/features/stepper/adventure";
import { resolveStepType } from "@/features/stepper/steptypes/registry";
import { useFunnelStore, useStepperStore } from "@/store";
import { fill } from "@/features/funnel/lib/copy";
import { genOpCode } from "@/features/funnel/lib/codenames";
import { reportStep } from "@/features/stepper/report-step";
import { bookStepTexts } from "@/features/funnel/api/use-mission-file";
import { useDailyDoors, missionIdFor } from "@/features/funnel/api/use-doors";
import type { StepperGraph, StepperStep } from "@/features/stepper/types";
import type { Door, Tier } from "@/features/funnel/types";

export const Route = createFileRoute("/choosemission/code/$code")({
  component: ChooseMission,
});

const TIERS: Tier[] = ["beginner", "challenging", "improbable"];

const message =
  "text-center font-mono text-[11px] tracking-[0.22em] text-muted-foreground uppercase";

/**
 * Pick the two screens out of whatever graph the code resolves to: the first
 * WebSmDoors step and the first WebSmDifficulty step.
 *
 * ponytail: by step TYPE, not step name — the choosemission steps aren't
 * authored yet, so this plays the funnel's pair today and the authored pair
 * the moment the adventure exists, with no name to keep in sync. If a flow
 * ever needs two doors screens, walk `branches` instead.
 */
function twoStepFlow(graph: StepperGraph): StepperStep[] {
  const byType = (stepType: string) =>
    Object.values(graph.steps).find((s) => s.stepType === stepType);
  return [byType("WebSmDoors"), byType("WebSmDifficulty")].filter(
    (s): s is StepperStep => !!s,
  );
}

/**
 * `/choosemission/code/<code>` — the short picker link. `<code>` is the
 * operative's mission codename, the same identity `/missionkit/code/<code>`
 * carries: it says WHO this link is for, not what to play. The adventure is
 * the one this app plays (the stepper store), because a codename in the URL
 * is not an adventure id and passing it to `loadStepperAdventure` silently
 * falls back to the offline fixture — a picker that looks right and writes
 * its picks against the wrong step ids. A numeric code is still honoured so
 * the links already texted out keep working.
 *
 * The visitor picks a door and a difficulty, and that's it. Unlike the funnel,
 * the envelope tap does NOT open the visitor's SMS app: the picks are recorded
 * and the handler texts them.
 *
 * Each screen reports itself to `record_journey_step` as it is answered —
 * per screen, not one blob at the end, because a visitor who picks a door and
 * leaves has still picked a door, and stepper rebuilds the run by replaying
 * journey rows. The picks otherwise stay local rather than going through the
 * funnel store: this link is its own run and must not disturb a funnel walk
 * the visitor may be mid-way through.
 */
function ChooseMission() {
  const { code } = Route.useParams();
  const referrerName = useFunnelStore((s) => s.referrerName);
  const [index, setIndex] = useState(0);
  const [door, setDoor] = useState<Door | null>(null);
  // true the first time only: flips in the tap itself (state would let a
  // double tap through)
  const [firstAccept] = useState(() => {
    let done = false;
    return () => !done && (done = true);
  });
  const navigate = useNavigate();

  // A numeric code is a legacy adventure-id link; anything else is a codename
  // and the adventure comes from the store.
  const configuredId = useStepperStore((s) => s.adventureId);
  const adventureId = /^\d+$/.test(code) ? code : configuredId;

  const { data: graph, isPending } = useQuery({
    queryKey: ["choosemission", adventureId],
    queryFn: () => loadStepperAdventure(adventureId),
    // fresh on each visit (the default): an author's edit to the steps must
    // not wait out a cached copy
  });
  // Today's doors — same query the doors screen renders its labels from, so
  // the MissionID reported below is the mission behind the label they saw.
  const { data: doors } = useDailyDoors();

  if (isPending) {
    return <p className={message}>Establishing secure line…</p>;
  }

  const steps = graph ? twoStepFlow(graph) : [];
  if (steps.length < 2) {
    return <p className={message}>Those orders could not be found.</p>;
  }

  if (index >= steps.length) {
    // The same beat as the funnel's Delivered screen — orders in, the handler
    // takes it from here — so it gets the same card rather than a bare line of
    // text. Rendered THROUGH the template, not restyled next to it, so the two
    // screens cannot drift apart. Only the copy differs: this is a later day's
    // case, and there is nothing to replay from a picker link, so no
    // ReplayLabel and no button. Colours ride along from the last step so the
    // card matches the flow the member just walked.
    const Delivered = resolveStepType("WebSmDelivered");
    const last = steps[steps.length - 1];
    const doneOpts: Record<string, string> = {
      Title: "ORDERS RECEIVED",
      Message:
        "Your handler will be reaching out to you shortly to brief you on this case.",
      BgColor: last.options.BgColor ?? "",
      AccentColor: last.options.AccentColor ?? "",
      GlyphColor: last.options.AccentColor ?? "",
    };
    if (Delivered) {
      return createElement(Delivered, {
        getOpt: (name: string) => doneOpts[name] ?? "",
        dispatch: () => {},
      });
    }
    return <p className={message}>Orders received.</p>;
  }

  const step = steps[index];
  // DeliverOnWeb: the mission is handed over in the browser, so this link is
  // the day's run — the picks go into the funnel store (the folder previews
  // the mission from them) and accepting hands off to the main player.
  const onWeb = steps[1].options.DeliverOnWeb === "1";
  const getOpt = (name: string): string =>
    fill(step.options[name] ?? "", { referrer: referrerName || "Ashley" });

  const dispatch = (eventV: string) => {
    if (!eventV.startsWith("CLICK")) return;
    const n = Number(eventV.slice(5)) || 1;

    if (step.stepType === "WebSmDoors") {
      const picked: Door = n === 1 ? "love" : "stranger";
      setDoor(picked);
      if (onWeb)
        useFunnelStore
          .getState()
          .pickDoor({ door: picked, opCode: genOpCode() });
      reportStep(graph!.adventureId, step, { Door: picked });
      setIndex(index + 1);
      return;
    }

    // Difficulty: the last screen. MissionID rides along so stepper loads the
    // exact mission that was behind the door, rather than re-deriving it from
    // door + tier and possibly disagreeing with the label they were shown.
    // once: a second tap must not report (or book) the accept again
    if (!firstAccept()) return;
    const tier = TIERS[n - 1] ?? "beginner";
    const missionId = missionIdFor(doors, door, tier);
    reportStep(graph!.adventureId, step, {
      Difficulty: tier,
      MissionID: String(missionId ?? ""),
    });
    const next = step.branches.find((b) => b.eventV === eventV)?.nextStep;
    if (onWeb && next) {
      useFunnelStore.getState().acceptMission({ tier, missionId });
      // the texts an accept books, as the player's accept does
      void bookStepTexts(graph!.adventureId, step.stepName, tier, missionId);
      // The player resumes from this pointer on load (same adventure, step
      // exists), so it opens on the mission-live screen.
      useStepperStore.setState({
        adventureId: graph!.adventureId,
        currentStepName: next,
      });
      void navigate({ to: "/" });
      return;
    }
    setIndex(index + 1);
  };

  const Template = resolveStepType(step.stepType);
  if (!Template) {
    return <p className={message}>No renderer for “{step.stepType}”.</p>;
  }
  // createElement (not <Template/>) — resolved dynamically from the step type.
  return createElement(Template, { getOpt, dispatch });
}
